/* eslint-disable */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, LogOut, Package, DollarSign, Plus, Trash2, Users, TrendingUp, Award, AlertTriangle, Zap } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import HistoryModal from "./HistoryModal";
import LanguageSwitcher from "./LanguageSwitcher";
import SupportCenter from "./SupportCenter";
import NotificationBell from "./NotificationBell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const countryNames = { usa: "États-Unis", dr: "République Dominicaine", haiti: "Haïti" };
const countryFlags = { usa: "🇺🇸", dr: "🇩🇴", haiti: "🇭🇹" };

const ManagerDashboardV2 = () => {
  const navigate = useNavigate();
  const [country, setCountry] = useState(null);
  const [managerName, setManagerName] = useState("");
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("mystock");
  const [hookahSessions, setHookahSessions] = useState([]);

  // Stock setup modal
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockModels, setStockModels] = useState([{ name: "", quantity: 0, price: 0 }]);

  // Sale modal
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [saleForm, setSaleForm] = useState({ vape_model: "", quantity_sold: 1, price_per_unit: 0 });

  // Sub-stocker add modal
  const [showAddSub, setShowAddSub] = useState(false);
  const [subForm, setSubForm] = useState({ name: "", quantity: 0, price: 0 });

  // Sale per sub-stocker
  const [activeSubSale, setActiveSubSale] = useState(null);
  const [subSaleQty, setSubSaleQty] = useState(1);

  // Registered stockers (real platform stockers assigned via manager_id)
  const [regStockers, setRegStockers] = useState(null);

  // History modal
  const [showHistory, setShowHistory] = useState(false);

  // Weekly/monthly report
  const [report, setReport] = useState(null);
  const [reportPeriod, setReportPeriod] = useState("week");

  // Manager CEO-level modification (per CEO request) — for assigned stockers
  const [modifyTarget, setModifyTarget] = useState(null); // stocker card object
  const [modifyForm, setModifyForm] = useState({ initial_stock: 0, current_stock: 0, commission_rate: 12.5, end_date: "", control_date: "", carryover_from_previous_cycle: 0, total_revenue: 0 });
  const [modifySaving, setModifySaving] = useState(false);

  const openModifyModal = (card) => {
    setModifyTarget(card);
    setModifyForm({
      initial_stock: card.initial_stock ?? 0,
      current_stock: card.current_stock ?? 0,
      commission_rate: card.commission_rate ?? 12.5,
      end_date: (card.end_date || "").substring(0, 10),
      control_date: (card.control_date || "").substring(0, 10),
      carryover_from_previous_cycle: card.carryover_from_previous_cycle ?? 0,
      total_revenue: card.total_revenue ?? 0,
    });
  };

  const submitModify = async () => {
    if (!modifyTarget) return;
    setModifySaving(true);
    try {
      const payload = {
        initial_stock: parseInt(modifyForm.initial_stock) || 0,
        current_stock: parseInt(modifyForm.current_stock) || 0,
        commission_rate: parseFloat(modifyForm.commission_rate) || 0,
        commission_override: true,
        carryover_from_previous_cycle: parseInt(modifyForm.carryover_from_previous_cycle) || 0,
        total_revenue: parseFloat(modifyForm.total_revenue) || 0,
      };
      if (modifyForm.end_date) payload.end_date = modifyForm.end_date;
      if (modifyForm.control_date) payload.control_date = modifyForm.control_date;
      await axios.put(`${API}/stockers/${modifyTarget.id}`, payload);
      // Log the manager action for CEO trace
      await axios.post(`${API}/support/chat`, {
        action: "report", user_id: data?.manager?.id, user_name: managerName,
        message: `[Manager modifikasyon] ${modifyTarget.name} → ${JSON.stringify(payload)}`,
      }).catch(() => {});
      toast.success("Modifikasyon aplike — CEO gen dosye a");
      setModifyTarget(null);
      axios.get(`${API}/manager/${data.manager.id}/registered-stockers`).then((r) => setRegStockers(r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erè modifikasyon");
    } finally {
      setModifySaving(false);
    }
  };

  useEffect(() => {
    const auth = localStorage.getItem("managerAuth");
    if (!auth) {
      navigate("/manager/login");
      return;
    }
    const { country: c, name } = JSON.parse(auth);
    setCountry(c);
    setManagerName(name || "Manager");
    fetchAll(c);
  }, [navigate]);

  // Auto-refresh every 30s
  useEffect(() => {
    if (!country) return;
    const tick = setInterval(() => fetchAll(country), 30000);
    return () => clearInterval(tick);
  }, [country]);

  const fetchAll = async (c) => {
    try {
      const res = await axios.get(`${API}/manager/dashboard/${c}`);
      setData(res.data);
    } catch (e) {
      console.log("manager dashboard error", e);
    }
    try {
      const hres = await axios.get(`${API}/hookah/daily-session/country/${c}/active`);
      setHookahSessions(hres.data || []);
    } catch (e) {}
  };

  // Fetch registered stockers + report whenever we have manager id
  useEffect(() => {
    const mid = data?.manager?.id;
    if (!mid) return;
    axios.get(`${API}/manager/${mid}/registered-stockers`).then((r) => setRegStockers(r.data)).catch(() => {});
    axios.get(`${API}/manager/${mid}/report?period=${reportPeriod}`).then((r) => setReport(r.data)).catch(() => {});
  }, [data?.manager?.id, reportPeriod]);

  const handleDeleteRegStocker = async (sid, sname) => {
    if (!window.confirm(`Konfime: efase stocker ${sname} sou platfòm la? CEO ap gen notifikasyon.`)) return;
    try {
      await axios.delete(`${API}/manager/${data.manager.id}/stocker/${sid}`);
      toast.success("Stocker efase");
      axios.get(`${API}/manager/${data.manager.id}/registered-stockers`).then((r) => setRegStockers(r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erè");
    }
  };

  // Refresh helper used by Rekalkile / Aktivasyon buttons
  const loadData = () => {
    if (country) fetchAll(country);
    const mid = data?.manager?.id;
    if (mid) axios.get(`${API}/manager/${mid}/registered-stockers`).then((r) => setRegStockers(r.data)).catch(() => {});
  };

  const handleLogout = () => {
    localStorage.removeItem("managerAuth");
    navigate("/");
  };

  const handleSaveStock = async () => {
    if (!data?.manager?.id) return;
    const cleanModels = stockModels
      .filter((m) => m.name && Number(m.quantity) > 0)
      .map((m) => ({ name: m.name.trim(), quantity: Number(m.quantity), price: Number(m.price) || 0 }));
    if (cleanModels.length === 0) {
      toast.error("Ajoute omwen yon modèl vape");
      return;
    }
    try {
      await axios.put(`${API}/manager/${data.manager.id}/stock`, { vape_models: cleanModels });
      toast.success("Stock pèsonèl anrejistre!");
      setShowStockModal(false);
      fetchAll(country);
    } catch (e) {
      toast.error("Erè anrejistreman");
    }
  };

  const handleRecordSale = async () => {
    if (!data?.manager?.id) return;
    if (!saleForm.vape_model || saleForm.quantity_sold <= 0) {
      toast.error("Chwazi modèl ak kantite");
      return;
    }
    try {
      await axios.post(`${API}/manager/${data.manager.id}/record-sale`, {
        vape_model: saleForm.vape_model,
        quantity_sold: Number(saleForm.quantity_sold),
        price_per_unit: Number(saleForm.price_per_unit) || 0,
      });
      toast.success("Vant anrejistre!");
      setShowSaleModal(false);
      setSaleForm({ vape_model: "", quantity_sold: 1, price_per_unit: 0 });
      fetchAll(country);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erè");
    }
  };

  const handleAddSub = async () => {
    if (!data?.manager?.id) return;
    if (!subForm.name.trim() || subForm.quantity <= 0) {
      toast.error("Non ak kantite obligatwa");
      return;
    }
    try {
      await axios.post(`${API}/manager/${data.manager.id}/sub-stockers`, {
        name: subForm.name.trim(),
        quantity: Number(subForm.quantity),
        price: Number(subForm.price) || 0,
      });
      toast.success("Sub-Stocker ajoute!");
      setShowAddSub(false);
      setSubForm({ name: "", quantity: 0, price: 0 });
      fetchAll(country);
    } catch (e) {
      toast.error("Erè");
    }
  };

  const handleSubSale = async () => {
    if (!activeSubSale || !data?.manager?.id) return;
    try {
      await axios.put(`${API}/manager/${data.manager.id}/sub-stockers/${activeSubSale.id}/sale`, {
        quantity_sold: Number(subSaleQty),
      });
      toast.success("Vant anrejistre!");
      setActiveSubSale(null);
      setSubSaleQty(1);
      fetchAll(country);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erè");
    }
  };

  const handleDeleteSub = async (sid) => {
    if (!data?.manager?.id) return;
    if (!window.confirm("Konfime: efase sub-stocker sa?")) return;
    try {
      await axios.delete(`${API}/manager/${data.manager.id}/sub-stockers/${sid}`);
      toast.success("Sub-stocker efase");
      fetchAll(country);
    } catch (e) {
      toast.error("Erè");
    }
  };

  if (!country || !data) return <div className="min-h-screen bg-zinc-950" />;

  const m = data.manager;
  const subs = data.sub_stockers || [];
  const agg = data.country_aggregate || { by_city: [], total_initial: 0, total_current: 0, total_sold: 0, stocker_count: 0 };

  // Chart data (anonymized — only city aggregates)
  const chartData = (agg.by_city || []).map((c) => ({
    city: c.city,
    sold: c.sold,
    remaining: c.current,
  }));

  return (
    <div className="min-h-screen bg-white relative" data-testid="manager-dashboard" style={{fontFamily: 'Inter, system-ui, sans-serif'}}>
      {/* VAPE-YARD TOPBAR */}
      <div className="bg-black border-b-4 border-amber-400 px-4 py-3 sticky top-0 z-30 flex items-center justify-between shadow-xl">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-amber-400 rounded-lg flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-black" />
          </div>
          <div>
            <p className="text-white font-black text-base tracking-wider leading-none">VAPE<span className="text-amber-400">-</span>YARD</p>
            <p className="text-zinc-500 text-[9px] uppercase tracking-[0.25em] mt-0.5">Manager · {countryNames[country] || country}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell userId={m.id} />
          <LanguageSwitcher />
          <div className="flex items-center gap-2 pl-2 pr-3 py-1 rounded-full bg-zinc-900 hidden sm:flex">
            <div className="w-7 h-7 rounded-full bg-amber-400 flex items-center justify-center text-black font-black text-xs">{(managerName||"M").charAt(0).toUpperCase()}</div>
            <div className="text-left leading-tight">
              <p className="text-white font-bold text-xs">{managerName}</p>
              <p className="text-amber-400 text-[9px]">{countryFlags[country]} Manager</p>
            </div>
          </div>
          <button onClick={handleLogout} className="p-2 text-zinc-400 hover:text-red-400 hover:bg-zinc-900 rounded-lg" data-testid="manager-logout" title="Sortir">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-5">
        {/* ACTIONS BAR */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2 bg-gradient-to-r from-zinc-900 to-black border-l-4 border-amber-400 rounded-xl px-4 py-3 shadow-md">
          <div>
            <p className="text-white font-black text-lg">Bienvenue, <span className="text-amber-400">{managerName}</span></p>
            <p className="text-zinc-400 text-xs">{countryFlags[country]} {countryNames[country]} — jere tout stockers ou yo</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={async () => {
                if (!window.confirm("Rekalkile chif sik la pou TOUT stockers + NM depi sales_history? Sa ap ranje nenpòt drift.")) return;
                try {
                  const r = await axios.post(`${API}/manager/recalc-all`);
                  toast.success(`✅ Rekalkile Tout fini: ${r.data.fixed_count} moun ranje, ${r.data.unchanged} te deja korèk`);
                  loadData?.();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Erè rekalkile tout");
                }
              }}
              data-testid="manager-recalc-all-btn"
              className="px-3 py-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-black text-xs font-black flex items-center gap-1"
            >
              🔄 <span className="hidden md:inline">Rekalkile Tout</span>
            </button>
            <ManagerPendingActivations onActivated={() => loadData?.()} />
            <button
              onClick={() => setShowHistory(true)}
              data-testid="manager-history-btn"
              className="px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-black"
            >
              📜 Istori
            </button>
          </div>
        </div>
        {showHistory && <HistoryModal userId={m.id} onClose={() => setShowHistory(false)} />}
        <SupportCenter userId={m.id} userName={m.name} />

        {/* $13 MANAGER BONUS CARD */}
        <div className="rounded-2xl p-5 mb-6 relative overflow-hidden border-2 border-amber-500/40" style={{background:'linear-gradient(135deg,#0f172a 0%,#1e293b 50%,#854d0e 100%)'}} data-testid="manager-bonus-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <div className="bg-amber-500/20 p-4 rounded-2xl border border-amber-400/40">
                <Award className="w-8 h-8 text-amber-400" />
              </div>
              <div>
                <p className="text-amber-300 text-[10px] tracking-[0.3em] uppercase font-bold">Bonus Manager</p>
                <p className="text-white font-black text-3xl">${(m.manager_card_bonus || 13).toFixed(2)}</p>
                <p className="text-amber-200/80 text-xs mt-1">Bonus chak sik — pèfòmans peyi ou: {agg.total_sold} vapes vann</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-zinc-400 text-[10px] uppercase">Bonus pèsonèl (vant ou)</p>
              <p className="text-emerald-400 font-bold text-2xl">${(m.bonus || 0).toFixed(2)}</p>
              <p className="text-zinc-500 text-[10px] mt-1">{m.sold} × $0.99</p>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="glass-panel p-4 text-center" data-testid="kpi-stock">
            <Package className="w-6 h-6 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{m.current_stock}/{m.initial_stock}</p>
            <p className="text-zinc-500 text-xs">Stock Pèsonèl</p>
          </div>
          <div className="glass-panel p-4 text-center" data-testid="kpi-sold">
            <TrendingUp className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-emerald-400">{m.sold}</p>
            <p className="text-zinc-500 text-xs">Vape Vann</p>
          </div>
          <div className="glass-panel p-4 text-center" data-testid="kpi-revenue">
            <DollarSign className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-400">${(m.revenue || 0).toFixed(0)}</p>
            <p className="text-zinc-500 text-xs">Revni</p>
          </div>
          <div className="glass-panel p-4 text-center" data-testid="kpi-subs">
            <Users className="w-6 h-6 text-violet-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{subs.length}</p>
            <p className="text-zinc-500 text-xs">Sub-Stockers</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button onClick={() => setActiveTab("mystock")} data-testid="tab-mystock"
            className={`px-4 py-2 rounded-xl text-sm font-bold ${activeTab === "mystock" ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
            📦 Stock Mwen
          </button>
          <button onClick={() => setActiveTab("subs")} data-testid="tab-subs"
            className={`px-4 py-2 rounded-xl text-sm font-bold ${activeTab === "subs" ? "bg-violet-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
            👥 Sub-Stockers ({subs.length})
          </button>
          <button onClick={() => setActiveTab("registered")} data-testid="tab-registered"
            className={`px-4 py-2 rounded-xl text-sm font-bold ${activeTab === "registered" ? "bg-cyan-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
            🛰️ Stockers Asiyen ({regStockers?.cards?.length || 0})
          </button>
          <button onClick={() => setActiveTab("report")} data-testid="tab-report"
            className={`px-4 py-2 rounded-xl text-sm font-bold ${activeTab === "report" ? "bg-amber-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
            📋 Rapò
          </button>
          <button onClick={() => setActiveTab("country")} data-testid="tab-country"
            className={`px-4 py-2 rounded-xl text-sm font-bold ${activeTab === "country" ? "bg-emerald-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
            📊 Apèsi Peyi a
          </button>
          <button onClick={() => setActiveTab("hookah")} data-testid="tab-hookah"
            className={`px-4 py-2 rounded-xl text-sm font-bold ${activeTab === "hookah" ? "bg-orange-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
            🔥 Hookah ({hookahSessions.length})
          </button>
        </div>

        {/* ===== MY STOCK ===== */}
        {activeTab === "mystock" && (
          <div className="glass-panel p-6" data-testid="mystock-panel">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-400" /> Stock Pèsonèl Mwen
              </h3>
              <div className="flex gap-2">
                <button onClick={() => { setStockModels(m.vape_models?.length ? m.vape_models.map(x => ({name: x.name, quantity: x.quantity, price: x.price})) : [{name:"", quantity:0, price:0}]); setShowStockModal(true); }} data-testid="setup-stock-btn"
                  className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Konfigire Stock
                </button>
                {m.initial_stock > 0 && (
                  <button onClick={() => { setSaleForm({ vape_model: m.vape_models?.[0]?.name || "", quantity_sold: 1, price_per_unit: m.vape_models?.[0]?.price || 0 }); setShowSaleModal(true); }} data-testid="record-sale-btn"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> Anrejistre Vant
                  </button>
                )}
              </div>
            </div>

            {m.vape_models?.length > 0 ? (
              <div className="space-y-2">
                {m.vape_models.map((v, i) => (
                  <div key={i} className="bg-zinc-900/60 p-3 rounded-xl flex items-center justify-between">
                    <div>
                      <p className="text-white font-bold">{v.name}</p>
                      <p className="text-zinc-500 text-xs">${v.price} / vape</p>
                    </div>
                    <div className="flex gap-4 text-right">
                      <div>
                        <p className="text-zinc-500 text-[10px] uppercase">Rete</p>
                        <p className="text-amber-400 font-bold">{v.quantity}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-[10px] uppercase">Vann</p>
                        <p className="text-emerald-400 font-bold">{v.sold || 0}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 text-zinc-500">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Ou poko gen stock pèsonèl. Konfigire stock ou pou kòmanse vann.</p>
              </div>
            )}

            {(m.sales_history || []).length > 0 && (
              <div className="mt-6">
                <p className="text-zinc-400 text-xs uppercase mb-2">Dènye Vant Mwen</p>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {[...(m.sales_history || [])].reverse().slice(0, 10).map((s, i) => (
                    <div key={i} className="bg-zinc-900/50 p-2 rounded-lg flex items-center justify-between text-xs">
                      <span className="text-white">{s.vape_model}</span>
                      <span className="text-emerald-400 font-bold">+{s.quantity_sold} × ${s.price_per_unit}</span>
                      <span className="text-zinc-500">{new Date(s.recorded_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== SUB-STOCKERS ===== */}
        {activeTab === "subs" && (
          <div className="glass-panel p-6" data-testid="subs-panel">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Users className="w-5 h-5 text-violet-400" /> Sub-Stockers Pèsonèl ({subs.length})
              </h3>
              <button onClick={() => setShowAddSub(true)} data-testid="add-sub-btn"
                className="bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-2">
                <Plus className="w-4 h-4" /> Ajoute Sub-Stocker
              </button>
            </div>

            {subs.length === 0 ? (
              <div className="text-center py-10 text-zinc-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Ou poko gen sub-stockers. Klike "Ajoute Sub-Stocker" pou kòmanse.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {subs.map((s) => {
                  const sold = (s.initial_stock || 0) - (s.current_stock || 0);
                  const pct = s.initial_stock > 0 ? (sold / s.initial_stock) * 100 : 0;
                  return (
                    <div key={s.id} className="bg-zinc-900/60 p-4 rounded-xl" data-testid={`sub-${s.id}`}>
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div>
                          <p className="text-white font-bold text-lg">{s.name}</p>
                          <p className="text-zinc-500 text-xs">${s.price}/vape</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => { setActiveSubSale(s); setSubSaleQty(1); }}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg">
                            💰 Vant
                          </button>
                          <button onClick={() => handleDeleteSub(s.id)}
                            className="bg-red-600 hover:bg-red-500 text-white text-xs font-bold px-2 py-1.5 rounded-lg">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-sm mb-2">
                        <div className="bg-blue-500/10 border border-blue-500/30 p-2 rounded-lg">
                          <p className="text-blue-400 text-[10px] uppercase">Stock</p>
                          <p className="text-white font-bold">{s.initial_stock}</p>
                        </div>
                        <div className="bg-emerald-500/10 border border-emerald-500/30 p-2 rounded-lg">
                          <p className="text-emerald-400 text-[10px] uppercase">Vann</p>
                          <p className="text-emerald-400 font-bold">{sold}</p>
                        </div>
                        <div className="bg-amber-500/10 border border-amber-500/30 p-2 rounded-lg">
                          <p className="text-amber-400 text-[10px] uppercase">Rete</p>
                          <p className="text-amber-400 font-bold">{s.current_stock}</p>
                        </div>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-400" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ===== REGISTERED STOCKERS (asiyen ak Manager ID) ===== */}
        {activeTab === "registered" && (
          <div className="glass-panel p-6" data-testid="registered-panel">
            <h3 className="text-white font-bold flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-cyan-400" /> Stockers Asiyen Ba Mwen — Vi Live
            </h3>
            <p className="text-zinc-500 text-xs mb-4">Lis stocker ki anrejistre sou platfòm la ki gen manadjè_id ki egal kòd manadjè a. Ventes yo monte tou kòm ventes manadjè a.</p>

            {regStockers ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3 text-center">
                    <p className="text-cyan-400 text-[10px] uppercase">Stockers</p>
                    <p className="text-white font-bold text-2xl">{regStockers.totals.stocker_count}</p>
                  </div>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 text-center">
                    <p className="text-emerald-400 text-[10px] uppercase">Total Vann</p>
                    <p className="text-emerald-400 font-bold text-2xl">{regStockers.totals.total_sold}</p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-center">
                    <p className="text-yellow-400 text-[10px] uppercase">Revni</p>
                    <p className="text-yellow-400 font-bold text-2xl">${(regStockers.totals.total_revenue || 0).toFixed(0)}</p>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-center">
                    <p className="text-amber-400 text-[10px] uppercase">Stock Rete</p>
                    <p className="text-amber-400 font-bold text-2xl">{regStockers.totals.total_current}</p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-center">
                    <p className="text-red-400 text-[10px] uppercase">Anreta</p>
                    <p className="text-red-400 font-bold text-2xl">{regStockers.totals.overdue_count}</p>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  {regStockers.cards.map((c) => (
                    <div key={c.id} className={`rounded-xl p-4 border ${c.is_overdue ? "bg-red-950/30 border-red-700" : "bg-zinc-900/60 border-zinc-700"}`} data-testid={`reg-card-${c.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <div>
                          <p className="text-white font-bold flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${c.current_stock > 0 ? "bg-emerald-400 animate-pulse" : "bg-zinc-600"}`} />
                            {c.name}
                          </p>
                          <p className="text-zinc-500 text-xs">📍 {c.city || "—"}</p>
                          {c.is_overdue && (
                            <p className="text-red-400 text-[11px] font-bold mt-1">⚠️ RETARD — {c.days_overdue}j sou kontwòl (jou gras rete: {c.grace_remaining})</p>
                          )}
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <div>
                            <p className="text-yellow-400 font-black text-lg">${(c.revenue || 0).toFixed(0)}</p>
                            <p className="text-zinc-500 text-[10px]">{c.progress_pct}% sold</p>
                          </div>
                          <button onClick={() => openModifyModal(c)} data-testid={`modify-reg-${c.id}`}
                            className="bg-blue-700 hover:bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded-lg" title="Modifye kòm CEO">
                            ✎ Modifye
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const r = await axios.post(`${API}/manager/recalc-user/${c.id}`);
                                const { before, after, diff, commission, commission_rate } = r.data;
                                const changed = Object.values(diff).some(v => v !== 0);
                                if (changed) {
                                  toast.success(`🔄 ${c.name} rekalkile: Vann ${before.total_sold}→${after.total_sold} • Revni $${before.total_revenue}→$${after.total_revenue} • Komisyon ${commission_rate}% = $${commission}`, { duration: 8000 });
                                } else {
                                  toast.success(`✅ ${c.name}: chif yo deja korèk`);
                                }
                                loadData?.();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Erè rekalkile");
                              }
                            }}
                            data-testid={`recalc-reg-${c.id}`}
                            className="bg-gradient-to-r from-cyan-700 to-fuchsia-700 hover:brightness-110 text-white text-[10px] font-bold px-2 py-1 rounded-lg" title="Rekalkile chif sik depi sales_history">
                            🔄
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-1 text-center text-[10px]">
                        <div><p className="text-zinc-500">Init</p><p className="text-white font-bold">{c.initial_stock}</p></div>
                        <div><p className="text-zinc-500">Vann</p><p className="text-emerald-400 font-bold">{c.sold}</p></div>
                        <div><p className="text-zinc-500">Rete</p><p className="text-amber-400 font-bold">{c.current_stock}</p></div>
                        <div><p className="text-zinc-500">Fen Sik</p><p className="text-zinc-300">{(c.end_date || "").substring(0, 10)}</p></div>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mt-2">
                        <div className="h-full" style={{ width: `${c.progress_pct}%`, background: c.progress_pct >= 75 ? "#10b981" : c.progress_pct >= 40 ? "#f59e0b" : "#ef4444" }} />
                      </div>
                    </div>
                  ))}
                  {regStockers.cards.length === 0 && (
                    <p className="text-zinc-500 text-center py-8">
                      Pa gen stocker asiyen ba ou poko. CEO dwe konekte stocker ak manadjè ID ou nan paramèt yo.
                    </p>
                  )}
                </div>

                {regStockers.activity.length > 0 && (
                  <div className="bg-zinc-900/40 rounded-xl p-3">
                    <p className="text-white font-bold text-xs uppercase mb-2">Aktivite Live</p>
                    <div className="space-y-1 max-h-[240px] overflow-y-auto">
                      {regStockers.activity.map((a, i) => (
                        <div key={i} className="bg-zinc-950/60 p-2 rounded-lg flex items-center justify-between text-xs">
                          <span className="text-white">{a.stocker_name}</span>
                          <span className="text-zinc-400">{a.model}</span>
                          <span className="text-emerald-400 font-bold">+{a.qty}</span>
                          <span className="text-zinc-500">{new Date(a.recorded_at).toLocaleDateString("fr-FR")}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-zinc-500 text-center py-8">Chajman...</p>
            )}
          </div>
        )}

        {/* ===== WEEKLY / MONTHLY REPORT ===== */}
        {activeTab === "report" && (
          <div className="glass-panel p-6" data-testid="report-panel">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-400" /> Rapò Pèfòmans
              </h3>
              <div className="flex gap-2">
                {[{ k: "week", l: "Semèn" }, { k: "month", l: "Mwa" }, { k: "all", l: "Tout Tan" }].map((p) => (
                  <button key={p.k} onClick={() => setReportPeriod(p.k)} data-testid={`period-${p.k}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold ${reportPeriod === p.k ? "bg-amber-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
                    {p.l}
                  </button>
                ))}
              </div>
            </div>

            {report ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 text-center">
                    <p className="text-blue-400 text-[10px] uppercase">Stock Deploye</p>
                    <p className="text-white font-bold text-xl">{report.stock_deployed}</p>
                  </div>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 text-center">
                    <p className="text-emerald-400 text-[10px] uppercase">Vape Vann</p>
                    <p className="text-emerald-400 font-bold text-xl">{report.sold}</p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-center">
                    <p className="text-yellow-400 text-[10px] uppercase">Revni</p>
                    <p className="text-yellow-400 font-bold text-xl">${(report.revenue || 0).toFixed(0)}</p>
                  </div>
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3 text-center">
                    <p className="text-cyan-400 text-[10px] uppercase">Stockers Aktif</p>
                    <p className="text-cyan-400 font-bold text-xl">{report.active_stocker_count}</p>
                  </div>
                </div>

                {report.top_sellers.length > 0 && (
                  <div className="bg-zinc-900/60 rounded-xl p-3 mb-3">
                    <p className="text-white font-bold text-xs uppercase mb-2">🏆 Top Vandè</p>
                    <div className="space-y-1">
                      {report.top_sellers.map((t, i) => (
                        <div key={i} className="flex items-center justify-between bg-zinc-950/50 p-2 rounded-lg text-xs">
                          <span className="text-white"><span className="text-amber-400 font-bold">#{i + 1}</span> {t.name}</span>
                          <span className="text-zinc-500">{t.city || "—"}</span>
                          <span className="text-emerald-400 font-bold">{t.sold} vape</span>
                          <span className="text-yellow-400">${t.revenue.toFixed(0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {report.stock_entries.length > 0 && (
                  <div className="bg-zinc-900/60 rounded-xl p-3">
                    <p className="text-white font-bold text-xs uppercase mb-2">📦 Stock Antre nan Peryòd la (dat + lè)</p>
                    <div className="space-y-1 max-h-[220px] overflow-y-auto">
                      {report.stock_entries.map((e, i) => (
                        <div key={i} className="bg-zinc-950/50 p-2 rounded-lg flex items-center justify-between text-xs">
                          <span className="text-white">{e.stocker_name}</span>
                          <span className="text-blue-400">+{e.qty} vape</span>
                          <span className="text-zinc-500">{new Date(e.recorded_at).toLocaleString("fr-FR")}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-zinc-500 text-center py-8">Chajman rapò...</p>
            )}
          </div>
        )}

        {/* ===== COUNTRY OVERVIEW (anonymized graph) ===== */}
        {activeTab === "country" && (
          <div className="glass-panel p-6" data-testid="country-panel">
            <h3 className="text-white font-bold flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-emerald-400" /> Apèsi Mouvman Stock — {countryFlags[country]} {countryNames[country]}
            </h3>
            <p className="text-zinc-500 text-xs mb-4">Vi anonim — done globalize sou tout vil yo nan peyi ou a (san detay ki estrikteman pou stocker yo).</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 text-center">
                <p className="text-blue-400 text-[10px] uppercase">Total Stockers</p>
                <p className="text-white font-bold text-xl">{agg.stocker_count}</p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 text-center">
                <p className="text-emerald-400 text-[10px] uppercase">Total Vann</p>
                <p className="text-emerald-400 font-bold text-xl">{agg.total_sold}</p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-center">
                <p className="text-amber-400 text-[10px] uppercase">Stock Aktyèl</p>
                <p className="text-amber-400 font-bold text-xl">{agg.total_current}</p>
              </div>
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-3 text-center">
                <p className="text-violet-400 text-[10px] uppercase">Stock Inisyal</p>
                <p className="text-violet-400 font-bold text-xl">{agg.total_initial}</p>
              </div>
            </div>

            {chartData.length > 0 ? (
              <div style={{ width: "100%", height: 280 }} data-testid="country-chart">
                <ResponsiveContainer>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="city" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46" }} />
                    <Bar dataKey="sold" name="Vann" fill="#10b981" />
                    <Bar dataKey="remaining" name="Rete" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-10 text-zinc-500">Pa gen done sou peyi a poko</div>
            )}

            {(data.recent_anonymized || []).length > 0 && (
              <div className="mt-6">
                <p className="text-zinc-400 text-xs uppercase mb-2">Aktivite Resan (Anonim)</p>
                <div className="space-y-1 max-h-[160px] overflow-y-auto">
                  {data.recent_anonymized.map((r, i) => (
                    <div key={i} className="bg-zinc-900/40 p-2 rounded-lg flex items-center justify-between text-xs">
                      <span className="text-zinc-400">📍 {r.city}</span>
                      <span className="text-white">{r.model}</span>
                      <span className="text-emerald-400 font-bold">+{r.qty}</span>
                      <span className="text-zinc-500">{new Date(r.recorded_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== HOOKAH ===== */}
        {activeTab === "hookah" && (
          <div className="glass-panel p-6" data-testid="hookah-panel">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-400" /> Sesyon Hookah Aktif — {countryFlags[country]}
            </h3>
            {hookahSessions.length === 0 ? (
              <p className="text-zinc-500 text-center py-8 text-sm">Pa gen sesyon hookah aktif kounye a.</p>
            ) : (
              <div className="space-y-3">
                {hookahSessions.map((ds) => (
                  <div key={ds.id} className="bg-zinc-900/60 p-3 rounded-xl">
                    <p className="text-white font-bold">{ds.stocker_name}</p>
                    <p className="text-zinc-500 text-xs">Responsab: <span className="text-emerald-400">{ds.responsable_name}</span></p>
                    <p className="text-yellow-400 font-bold mt-1">${(ds.total_revenue || 0).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== STOCK SETUP MODAL ===== */}
      {showStockModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="stock-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-white font-bold mb-4">📦 Konfigire Stock Pèsonèl</h3>
            <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto">
              {stockModels.map((m, i) => (
                <div key={i} className="flex gap-2">
                  <input value={m.name} onChange={(e) => { const a = [...stockModels]; a[i].name = e.target.value; setStockModels(a); }}
                    placeholder="Modèl" className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-2 text-white text-sm flex-1" />
                  <input type="number" value={m.quantity} onChange={(e) => { const a = [...stockModels]; a[i].quantity = e.target.value; setStockModels(a); }}
                    placeholder="Qty" className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-2 text-white text-sm w-20" />
                  <input type="number" value={m.price} onChange={(e) => { const a = [...stockModels]; a[i].price = e.target.value; setStockModels(a); }}
                    placeholder="$" className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-2 text-white text-sm w-20" />
                  {stockModels.length > 1 && (
                    <button onClick={() => setStockModels(stockModels.filter((_, j) => j !== i))} className="text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button onClick={() => setStockModels([...stockModels, { name: "", quantity: 0, price: 0 }])}
              className="text-emerald-400 text-xs mb-4">+ Ajoute yon lòt modèl</button>
            <div className="flex gap-2">
              <button onClick={() => setShowStockModal(false)} className="flex-1 bg-zinc-700 text-white py-2 rounded-lg text-sm">Anile</button>
              <button onClick={handleSaveStock} data-testid="save-stock-btn"
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-2 rounded-lg text-sm font-bold">Anrejistre</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SALE MODAL ===== */}
      {showSaleModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="sale-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-white font-bold mb-4">💰 Anrejistre Vant Pèsonèl</h3>
            <select value={saleForm.vape_model} onChange={(e) => { const sel = m.vape_models?.find(v => v.name === e.target.value); setSaleForm({...saleForm, vape_model: e.target.value, price_per_unit: sel?.price || 0}); }}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mb-3">
              <option value="">— Chwazi modèl —</option>
              {m.vape_models?.map((v) => (<option key={v.name} value={v.name}>{v.name} ({v.quantity} rete)</option>))}
            </select>
            <input type="number" value={saleForm.quantity_sold} onChange={(e) => setSaleForm({...saleForm, quantity_sold: e.target.value})}
              placeholder="Kantite" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mb-3" />
            <input type="number" value={saleForm.price_per_unit} onChange={(e) => setSaleForm({...saleForm, price_per_unit: e.target.value})}
              placeholder="Pri / vape" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mb-4" />
            <div className="flex gap-2">
              <button onClick={() => setShowSaleModal(false)} className="flex-1 bg-zinc-700 text-white py-2 rounded-lg text-sm">Anile</button>
              <button onClick={handleRecordSale} data-testid="confirm-sale-btn"
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-2 rounded-lg text-sm font-bold">Konfime</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== ADD SUB-STOCKER MODAL ===== */}
      {showAddSub && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="add-sub-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-white font-bold mb-4">👥 Ajoute Sub-Stocker</h3>
            <input value={subForm.name} onChange={(e) => setSubForm({...subForm, name: e.target.value})}
              placeholder="Non sub-stocker" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mb-3" />
            <input type="number" value={subForm.quantity} onChange={(e) => setSubForm({...subForm, quantity: e.target.value})}
              placeholder="Kantite vape" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mb-3" />
            <input type="number" value={subForm.price} onChange={(e) => setSubForm({...subForm, price: e.target.value})}
              placeholder="Pri / vape" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mb-4" />
            <div className="flex gap-2">
              <button onClick={() => setShowAddSub(false)} className="flex-1 bg-zinc-700 text-white py-2 rounded-lg text-sm">Anile</button>
              <button onClick={handleAddSub} data-testid="confirm-add-sub-btn"
                className="flex-1 bg-violet-600 hover:bg-violet-500 text-white py-2 rounded-lg text-sm font-bold">Ajoute</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SUB-STOCKER SALE MODAL ===== */}
      {activeSubSale && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="sub-sale-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-white font-bold mb-4">💰 Vant pou {activeSubSale.name}</h3>
            <p className="text-zinc-500 text-xs mb-3">Rete: <span className="text-amber-400 font-bold">{activeSubSale.current_stock}</span></p>
            <input type="number" value={subSaleQty} onChange={(e) => setSubSaleQty(e.target.value)} min="1" max={activeSubSale.current_stock}
              placeholder="Kantite vann" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mb-4" />
            <div className="flex gap-2">
              <button onClick={() => setActiveSubSale(null)} className="flex-1 bg-zinc-700 text-white py-2 rounded-lg text-sm">Anile</button>
              <button onClick={handleSubSale} data-testid="confirm-sub-sale-btn"
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-2 rounded-lg text-sm font-bold">Konfime</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MANAGER CEO-LEVEL MODIFY MODAL ===== */}
      {modifyTarget && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="manager-modify-modal" onClick={() => setModifyTarget(null)}>
          <div className="bg-zinc-900 border border-blue-500/40 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-bold text-lg">✎ Modifye kòm CEO</h3>
                <p className="text-blue-300 text-xs">{modifyTarget.name} • {modifyTarget.city || "—"}</p>
                <p className="text-zinc-500 text-[10px] mt-1">Sou demand CEO — tout aksyon ap voye nan dosye a.</p>
              </div>
              <button onClick={() => setModifyTarget(null)} className="text-zinc-500 hover:text-white text-xl leading-none">×</button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-zinc-400">
                  Stock inisyal
                  <input type="number" value={modifyForm.initial_stock} onChange={(e) => setModifyForm({...modifyForm, initial_stock: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mt-1" data-testid="modify-initial-stock" />
                </label>
                <label className="text-xs text-zinc-400">
                  Stock aktyèl
                  <input type="number" value={modifyForm.current_stock} onChange={(e) => setModifyForm({...modifyForm, current_stock: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mt-1" data-testid="modify-current-stock" />
                </label>
                <label className="text-xs text-zinc-400">
                  Komisyon %
                  <input type="number" step="0.01" value={modifyForm.commission_rate} onChange={(e) => setModifyForm({...modifyForm, commission_rate: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mt-1" data-testid="modify-commission" />
                </label>
                <label className="text-xs text-zinc-400">
                  Total revni ($)
                  <input type="number" step="0.01" value={modifyForm.total_revenue} onChange={(e) => setModifyForm({...modifyForm, total_revenue: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mt-1" data-testid="modify-revenue" />
                </label>
                <label className="text-xs text-zinc-400">
                  Dat fen sik
                  <input type="date" value={modifyForm.end_date} onChange={(e) => setModifyForm({...modifyForm, end_date: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mt-1" data-testid="modify-end-date" />
                </label>
                <label className="text-xs text-zinc-400">
                  Dat kontwòl
                  <input type="date" value={modifyForm.control_date} onChange={(e) => setModifyForm({...modifyForm, control_date: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mt-1" data-testid="modify-control-date" />
                </label>
                <label className="text-xs text-zinc-400 col-span-2">
                  Carryover (sik pase — jaune abriko)
                  <input type="number" value={modifyForm.carryover_from_previous_cycle} onChange={(e) => setModifyForm({...modifyForm, carryover_from_previous_cycle: e.target.value})}
                    className="w-full bg-zinc-800 border-2 border-amber-500/50 rounded-lg px-3 py-2 text-amber-300 text-sm mt-1 font-bold" data-testid="modify-carryover" />
                </label>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setModifyTarget(null)} className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg text-sm" data-testid="modify-cancel-btn">Anile</button>
                <button onClick={submitModify} disabled={modifySaving} className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-bold" data-testid="modify-save-btn">
                  {modifySaving ? "Ap sove..." : "✓ Sove"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VAPE-YARD FOOTER */}
      <footer className="bg-black border-t border-zinc-900 px-4 lg:px-8 py-5 mt-6" data-testid="manager-footer">
        <div className="flex flex-wrap items-center justify-between gap-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-amber-400 rounded-lg flex items-center justify-center"><BarChart3 className="w-4 h-4 text-black" /></div>
            <div>
              <p className="text-white font-black text-sm tracking-wider leading-none">VAPE<span className="text-amber-400">-</span>YARD</p>
              <p className="text-zinc-600 text-[9px] uppercase tracking-[0.25em] mt-0.5">Flavors meet the future</p>
            </div>
          </div>
          <p className="text-zinc-500 text-[10px] font-semibold">© {new Date().getFullYear()} VAPE-YARD Enterprise · Manager Zone</p>
        </div>
      </footer>
    </div>
  );
};

// ==================== Manager Pending NM Activations ====================
const ManagerPendingActivations = ({ onActivated }) => {
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [activating, setActivating] = useState(null);
  const [showCode, setShowCode] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/manager/pending-nm-activations`);
      setPending(r.data.pending || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const activate = async (sub) => {
    setActivating(sub.id);
    try {
      const r = await axios.post(`${API}/manager/nm-stocker/${sub.id}/activate`);
      setShowCode({ sub_id: sub.id, code: r.data.activation_code, name: sub.name, marketer: sub.parent_marketer_name });
      toast.success(`${sub.name} aktive ✅`);
      load();
      onActivated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erè aktivasyon");
    } finally {
      setActivating(null);
    }
  };

  const count = pending.length;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="manager-pending-activations-btn"
        className="relative flex items-center gap-1.5 px-3 py-2 bg-amber-600/20 hover:bg-amber-600/40 border border-amber-500/40 rounded-xl text-amber-300 font-bold text-xs uppercase tracking-widest transition-colors"
        title="NM sub-stockers ki tann aktivasyon"
      >
        <Zap className="w-4 h-4" />
        <span className="hidden md:inline">Aktivasyon</span>
        {count > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-black text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            {count}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[95] bg-black/85 p-4 overflow-y-auto" data-testid="manager-pending-activations-modal" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className="max-w-3xl mx-auto bg-zinc-900 border-2 border-amber-500/40 rounded-2xl p-6 my-4 relative">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 bg-zinc-800 hover:bg-red-600 text-white p-2 rounded-full">✕</button>
            <h4 className="text-white font-bold text-xl mb-1">⏳ Aktivasyon Sub-Stockers NM</h4>
            <p className="text-zinc-500 text-xs mb-4">Network Marketers yo mete moun sa yo. Peze <span className="text-amber-400 font-bold">Aktive Kòd</span> pou jenere kòd konektasyon epi bay moun nan dashboard nòmal li.</p>

            {pending.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-zinc-400">Pa gen okenn aktivasyon k ap tann.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pending.map(sub => {
                  const total = (sub.vape_models || []).reduce((a,m) => a + (parseInt(m.quantity)||0), 0);
                  return (
                    <div key={sub.id} className="bg-zinc-950/60 border border-amber-500/20 rounded-xl p-3 flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <p className="text-white font-bold">
                          {sub.name}
                          <span className="ml-2 text-[10px] uppercase bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">activation pending</span>
                        </p>
                        <p className="text-zinc-400 text-xs mt-0.5">
                          Anba: <span className="text-cyan-300 font-bold">{sub.parent_marketer_name || "—"}</span>
                          {" • "} {sub.parent_marketer_country ? sub.parent_marketer_country.toUpperCase() : ""}
                        </p>
                        <p className="text-zinc-500 text-[11px] mt-1">
                          {total} vape total {(sub.vape_models||[]).length > 0 && `— ${sub.vape_models.map(m => `${m.name} (${m.quantity} × $${m.price})`).join(', ')}`}
                        </p>
                      </div>
                      <button
                        onClick={() => activate(sub)}
                        disabled={activating === sub.id}
                        data-testid={`manager-activate-sub-${sub.id}`}
                        className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:brightness-110 disabled:opacity-60 text-white font-black px-4 py-2 rounded-lg text-xs uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-emerald-500/20"
                      >
                        {activating === sub.id ? "Ap kreye kòd..." : "Aktive Kòd"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Code reveal modal */}
      {showCode && (
        <div className="fixed inset-0 z-[110] bg-black/95 flex items-center justify-center p-4" data-testid="manager-code-reveal-modal">
          <div className="bg-gradient-to-br from-emerald-950 to-cyan-950 border-2 border-emerald-500 rounded-2xl p-6 max-w-md w-full text-center">
            <p className="text-emerald-300 text-sm mb-2">✅ {showCode.name} aktive anba {showCode.marketer}</p>
            <p className="text-zinc-400 text-xs mb-4">Kòd konektasyon pou bay moun nan:</p>
            <p className="text-white font-black text-5xl tracking-widest mb-4">{showCode.code}</p>
            <button onClick={() => setShowCode(null)} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-6 py-2 rounded-xl">Fèmen</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ManagerDashboardV2;
